import type { DocumentData, Timestamp } from 'firebase/firestore'
import type { Business, User, UserRole } from '@/types'

/**
 * Firestore → admin view-model mapping.
 *
 * Users and circles are written by several clients over time: sign-up and the apps
 * write camelCase (`name`, `businessId`, `createdAt`), older code and earlier admin
 * builds wrote snake_case (`first_name`, `business_id`, `created_at`). Every reader
 * goes through here so the pages never have to know (CROSS_PLATFORM_CONTRACTS.md:
 * camelCase first, snake_case fallback).
 */

const KNOWN_ROLES: UserRole[] = ['care_recipient', 'caregiver', 'admin']

/** First non-empty string among the given fields. */
function pickString(data: DocumentData, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string' && value.trim() !== '') return value.trim()
  }
  return undefined
}

/** First field holding a Firestore Timestamp. */
export function pickTimestamp(data: DocumentData, ...keys: string[]): Timestamp | undefined {
  for (const key of keys) {
    const value = data[key]
    if (value && typeof value.toDate === 'function' && typeof value.toMillis === 'function') {
      return value as Timestamp
    }
  }
  return undefined
}

/** First present value among a field's spellings (used by generic previews). */
export function pickValue(data: DocumentData, keys: string[]): unknown {
  for (const key of keys) {
    if (data[key] !== undefined && data[key] !== null) return data[key]
  }
  return undefined
}

export function toUser(id: string, data: DocumentData): User {
  const firstName = pickString(data, 'firstName', 'first_name')
  const lastName = pickString(data, 'lastName', 'last_name')
  const joined = [firstName, lastName].filter(Boolean).join(' ')
  const email = pickString(data, 'email') ?? ''
  const isActive = data.isActive ?? data.is_active

  return {
    id,
    email,
    displayName: joined || pickString(data, 'name', 'displayName', 'display_name') || email,
    firstName,
    lastName,
    role: KNOWN_ROLES.includes(data.role) ? data.role : 'caregiver',
    businessId: pickString(data, 'businessId', 'business_id') ?? '',
    subscriptionTier: pickString(data, 'subscriptionTier', 'subscription_tier'),
    isActive: isActive !== false,
    requiresPasswordChange: (data.requiresPasswordChange ?? data.requires_password_change) === true,
    createdAt: pickTimestamp(data, 'createdAt', 'created_at'),
    lastLogin: pickTimestamp(data, 'lastLogin', 'last_login', 'lastLoginAt'),
  }
}

export function toBusiness(id: string, data: DocumentData): Business {
  return {
    id,
    name: pickString(data, 'name') ?? id,
    ownerId: pickString(data, 'ownerId', 'owner_id', 'owner_user_id'),
    subscription: data.subscription ?? undefined,
    usage_stats: data.usage_stats ?? data.usageStats ?? undefined,
    createdAt: pickTimestamp(data, 'createdAt', 'created_at'),
  }
}

/** Two-letter initials for an avatar; '?' when there is no name at all. */
export function initialsOf(name: string): string {
  const words = name.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean)
  if (words.length === 0) return '?'
  const letters = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[words.length - 1][0]
  return letters.toUpperCase()
}

/** Newest first; documents without a timestamp go last. */
export function byCreatedAtDesc<T extends { createdAt?: Timestamp }>(a: T, b: T): number {
  return (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0)
}
